/**
 * Everything downloading on this machine, in one place (`04-launcher-and-patcher.md` section 2.3).
 *
 * Before this, each InstallButton listened for its own game's events and forgot them the moment
 * the screen unmounted: walking away from a game page and coming back showed an Install button
 * over a download that was still running, because the events that would have said otherwise had
 * all been and gone. A queue has to outlive the screens looking at it, so it lives here.
 *
 * Two sources, and they answer different questions. `downloads_list` is the truth about what is
 * queued and how far along it is, and it can be asked at any moment — which is what lets a
 * screen opened halfway through a 30 GB download draw it correctly. `job://progress` is the
 * same numbers arriving four to eight times a second, which is what makes the bar move.
 */

import * as React from 'react';
import { capabilities } from '../platform/index.js';
import {
  native,
  onNative,
  type JobProgress,
  type JobState,
  type NativeError,
  type QueuedJob,
} from '../platform/tauri.js';

/** A download that finished while the app was open, kept so the screen can say so. */
export interface FinishedDownload {
  gameId: string;
  slug: string;
  state: Extract<JobState, 'installed' | 'not_installed' | 'error' | 'locked'>;
  at: number;
  error: NativeError | null;
}

/** What one game's job looks like to a screen, whether or not it is in the queue. */
export interface GameJob {
  state: JobState | null;
  progress: JobProgress | null;
  error: NativeError | null;
  /** Set while this game is in the download queue. */
  queued: QueuedJob | null;
}

export interface DownloadsSnapshot {
  /** The queue in the order it will be worked, first one first. */
  queue: QueuedJob[];
  finished: FinishedDownload[];
  byGame: Record<string, GameJob>;
  /** False until the first `downloads_list` has come back. */
  loaded: boolean;
}

const EMPTY: DownloadsSnapshot = { queue: [], finished: [], byGame: {}, loaded: false };

/** How many finished downloads the screen remembers. Older ones are in the library. */
const FINISHED_KEPT = 8;

/** The states that mean a job is over, whatever it was. */
const TERMINAL: ReadonlySet<JobState> = new Set<JobState>([
  'installed',
  'not_installed',
  'error',
  'locked',
]);

/**
 * The states a job has to have been in for its ending to be worth listing.
 *
 * Uninstalling ends in `not_installed` too, and a game somebody removed on purpose has no
 * business appearing under Finished as though a download had been cancelled.
 */
const WAS_DOWNLOADING: ReadonlySet<JobState> = new Set<JobState>([
  'queued',
  'downloading',
  'installing',
  'paused',
]);

/** Live event state per game, which covers jobs the queue never holds — verify and uninstall. */
export interface LiveJob {
  state: JobState | null;
  progress: JobProgress | null;
  error: NativeError | null;
}

/**
 * Puts the two sources together into what screens read.
 *
 * The order matters. A paused job is paused whatever the last event said, because the queue is
 * what decides whether it will be picked up again and the events only say what happened before
 * that. Everything else prefers the event, which is newer than the last list refresh.
 */
export function mergeGameJobs(
  queue: readonly QueuedJob[],
  live: ReadonlyMap<string, LiveJob>,
): Record<string, GameJob> {
  const merged: Record<string, GameJob> = {};
  const gameIds = new Set([...live.keys(), ...queue.map((job) => job.gameId)]);

  for (const gameId of gameIds) {
    const current = live.get(gameId) ?? { state: null, progress: null, error: null };
    const queued = queue.find((job) => job.gameId === gameId) ?? null;
    merged[gameId] = {
      state: queued?.paused ? 'paused' : (current.state ?? queued?.state ?? null),
      progress: current.progress,
      error: current.error ?? queued?.error ?? null,
      queued,
    };
  }
  return merged;
}

/**
 * Whether a job that just ended is worth listing under Finished.
 *
 * Uninstalling ends in `not_installed` too, and a game somebody removed on purpose has no
 * business appearing there as though a download had been cancelled.
 */
export function endsADownload(previous: JobState | null, next: JobState): boolean {
  return TERMINAL.has(next) && previous !== null && WAS_DOWNLOADING.has(previous);
}

let queue: QueuedJob[] = [];
let finished: FinishedDownload[] = [];
const live = new Map<string, LiveJob>();

let snapshot: DownloadsSnapshot = EMPTY;
const listeners = new Set<() => void>();
let started = false;

function supported(): boolean {
  return capabilities.installWindowsGames || capabilities.installAndroidGames;
}

function liveFor(gameId: string): LiveJob {
  return live.get(gameId) ?? { state: null, progress: null, error: null };
}

/**
 * Rebuilds the object screens read.
 *
 * `useSyncExternalStore` compares snapshots by identity, so this replaces the whole thing every
 * time rather than editing it in place.
 */
function publish(loaded = snapshot.loaded): void {
  snapshot = { queue, finished, byGame: mergeGameJobs(queue, live), loaded };
  for (const listener of listeners) listener();
}

/** Asks the native side for the queue. Cheap: it is an in-memory list, not a disk read. */
async function refresh(): Promise<void> {
  if (!supported()) return;
  try {
    queue = await native.downloadsList();
  } catch {
    queue = [];
  }
  publish(true);
}

function applyState(event: { gameId: string; state: JobState; error?: NativeError }): void {
  const current = liveFor(event.gameId);
  const over = TERMINAL.has(event.state);
  live.set(event.gameId, {
    state: event.state,
    // A finished job's last byte count belongs to the job, not to the game it left behind.
    progress: over ? null : current.progress,
    error: event.error ?? null,
  });

  if (endsADownload(current.state, event.state)) {
    const slug = queue.find((job) => job.gameId === event.gameId)?.slug ?? '';
    finished = [
      {
        gameId: event.gameId,
        slug,
        state: event.state as FinishedDownload['state'],
        at: Date.now(),
        error: event.error ?? null,
      },
      ...finished.filter((entry) => entry.gameId !== event.gameId),
    ].slice(0, FINISHED_KEPT);
  }

  publish();
}

function applyProgress(progress: JobProgress): void {
  const current = liveFor(progress.gameId);
  live.set(progress.gameId, { ...current, progress });

  // The queue's own copy is kept in step between refreshes, so the downloads screen draws a
  // moving bar rather than one that only steps when a phase changes.
  queue = queue.map((job) =>
    job.jobId === progress.jobId
      ? {
          ...job,
          phase: progress.phase,
          bytesDone: progress.bytesDone,
          bytesTotal: progress.bytesTotal,
          speedBps: progress.speedBps,
          etaS: progress.etaS,
        }
      : job,
  );
  publish();
}

/** Starts listening, once, for the lifetime of the app. */
function start(): void {
  if (started || !supported()) return;
  started = true;

  void refresh();

  void onNative('job://state', (event) => {
    applyState(event);
    // The state event says what happened; the list says what the queue looks like now that it
    // has. Only state changes move the queue, so this is the only place it is re-read.
    void refresh();
  });

  void onNative('job://progress', applyProgress);

  // A game that is running is not downloading, and the InstallButton draws both, so the two
  // live together rather than in a listener each screen sets up for itself.
  void onNative('game://running', (event) => {
    live.set(event.gameId, { ...liveFor(event.gameId), state: 'running' });
    publish();
  });

  void onNative('game://exited', (event) => {
    live.set(event.gameId, { state: null, progress: null, error: null });
    publish();
  });

  // Android can install and remove packages outside the app, and a job that ended while the
  // window was hidden left its event unheard.
  const recheck = () => void refresh();
  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', recheck);
}

function subscribe(listener: () => void): () => void {
  start();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): DownloadsSnapshot {
  return snapshot;
}

/** Everything in the queue, plus what finished while the app has been open. */
export function useDownloads(): DownloadsSnapshot {
  return React.useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

/** One game's job, or an empty one when it has none. */
export function useGameJob(gameId: string): GameJob {
  const { byGame } = useDownloads();
  return byGame[gameId] ?? { state: null, progress: null, error: null, queued: null };
}

/** The number the rail badges: downloads waiting or in progress. */
export function activeCount(snapshot: DownloadsSnapshot): number {
  return snapshot.queue.length;
}

/** Re-reads the queue. For the few places that change it without an event, like reordering. */
export function refreshDownloads(): void {
  void refresh();
}

/** Clears the finished list once somebody has seen it. */
export function clearFinished(): void {
  finished = [];
  publish();
}
